/**
 * The unified auth context object and its value type — the part of
 * `UnifiedAuthContext.tsx` a hook can read without that module's `useAuth`.
 *
 * Separate so that `useOptionalAuth` reaches the same context the providers
 * fill even where a test replaces `@/contexts/UnifiedAuthContext` with a
 * stand-in `useAuth`: the screens' tests do that wholesale, and a shared hook
 * below every dashboard (`useWeekStartsOn`, #864) must not depend on each of
 * those stand-ins knowing about it.
 */
import { createContext, useContext } from 'react';
import type { ApiClient } from '@/lib/api';
import type { GlobalRole } from '@aica/core/rbac';
import type { WeekStartsOn } from '@aica/core';

export interface UnifiedAuthContextValue {
  /** API client for making authenticated requests */
  apiClient: ApiClient;
  /** Current organization ID */
  orgId: string | null;
  /** User's role in the organization */
  role: GlobalRole | null;
  /** Whether auth data is still loading */
  isLoading: boolean;
  /**
   * The organization is being switched: what was keyed by the old one must not
   * be read for the new one yet. Always `false` in the embedded tree, which has
   * one organization for the whole session.
   */
  isOrgSwitching: boolean;
  /** Error message if auth failed */
  error: string | null;
  /**
   * The organization's first day of the week (#864), from the payload every
   * member already loads for the organization — the `/users/me` roster
   * (`ClerkAuthProvider`) or the embedded session (`EmbeddedAuthProvider`).
   * Not from `GET /api/v1/settings`: that one is client/admin only, and an
   * agent's or supervisor's Week would fall back to Monday while the server
   * cuts their weekly series on the configured day. `null`/absent = not
   * known yet; `useWeekStartsOn` reads Monday until then.
   */
  weekStartsOn?: WeekStartsOn | null;
}

export const UnifiedAuthContext = createContext<UnifiedAuthContextValue | null>(null);

/**
 * `useAuth` for a hook that also runs where no auth provider is mounted (a
 * component rendered on its own in a test): `null` there instead of a throw.
 * The app tree always has a provider (`ClerkAuthProvider` or
 * `EmbeddedAuthProvider`), so in the product this is `useAuth()`.
 */
export function useOptionalAuth(): UnifiedAuthContextValue | null {
  return useContext(UnifiedAuthContext);
}
